import { useState, useEffect } from 'react';
import { Pencil, Lock, Radio } from 'lucide-react';
import type { RevealCard, DayFixture } from '@/types/reveal';
import { formatFixtureStatus, formatFixtureTime } from '@/hooks/useDailyReveal';
import { getPpdCreditLabel } from '@/utils/ppdCreditLabel';
import { formatCountdown } from '@/utils/formatCountdown';
import { toTeamAbbreviation } from '@/utils/teamAbbreviation';
import { PlayerPickAvatar } from './PlayerPickAvatar';
import PropStatProgressTracker from './PropStatProgressTracker';
import PropOutcomeIcon from './PropOutcomeIcon';
import SportsbookBetslipCta from './SportsbookBetslipCta';
import PropProgressCelebrationOverlay from './PropProgressCelebrationOverlay';
import { getDailyRevealConfig } from '@low6-pickem/sports-config';
import { getJerseyAvatarUrl } from '@/utils/jerseyAvatar';
import {
  formatPropResultLabel,
  getPropTrackerBorderClass,
  getPropTrackerDisplayParts,
  resolvePropTrackerOutcome,
  resolvePropTrackerThreshold,
  resolvePropTrackerOperator,
} from '@/utils/propBasedDailyReveal';
import {
  buildPropProgressSeenKey,
  inferCelebrationStatType,
} from '@/utils/propProgressCelebration';
import { usePropProgressCelebration } from '@/hooks/usePropProgressCelebration';
import { LIVE_GOLD, LIVE_GOLD_BORDER_GRADIENT } from '@/utils/liveTrackerTheme';
import { SPORTSBOOK_BETSLIP_ENABLED } from '@/utils/sportsbookBetslip';
import { FitText } from '@/components/common/FitText';

/**
 * The player name shrinks to fit its column rather than being clipped, so these
 * are the ends of its range: the size a short name still gets, and the floor
 * that a long one ("J. SMITH-NJIGBA") is allowed to reach. The floor is set
 * where the condensed face is still comfortably readable on a phone; nothing
 * the NFL rosters contain needs to go below it in the space the card gives.
 */
const PLAYER_NAME_MAX_FONT_SIZE = 22;
const PLAYER_NAME_MIN_FONT_SIZE = 13;

export type UpcomingPickCardProps = {
  pick: RevealCard;
  dateLabel: string;
  totalHits: number;
  /** Hits auto-credited because the pick's game was postponed/cancelled (DKPLAT-478). */
  ppdHits?: number;
  /** Why the credit was granted ('postponed' | 'cancelled'), persisted at grading time. */
  ppdStatus?: string | null;
  fixture: DayFixture | null;
  entryStatus: 'revealed' | 'picked' | 'graded' | null;
  isPickCutoffReached?: boolean;
  onEdit?: () => void;
  /** Matches contest `league_ip`: headshots + team logos instead of jersey-only art. */
  leagueIpEnabled?: boolean;
  /** NFL prop-based mode. */
  isPropBased?: boolean;
  propResultStatus?: string | null;
  propResultValue?: number | null;
  propDisplayLabel?: string | null;
  propThreshold?: number | null;
  propOperator?: string | null;
  /** Entry + day identity so scoring Lotties persist across return visits. */
  entryId?: string | null;
  dayId?: string | null;
  /** Contest league code (`pickems.sport`) — selects the DK Sportsbook CMS page. */
  contestSport?: string | null;
  /** Sportsbook selection ids for the outcomes in this submitted entry. */
  sportsbookSelectionIds?: readonly string[];
  /** Outer spacing — section lists stack cards with mb-3; debug wrappers may use none. */
  className?: string;
};

const HITS_BADGE_STYLE = {
  fontFamily: '"Saira Extra Condensed", sans-serif',
  fontWeight: 700,
  fontSize: '18.75px',
  lineHeight: 1,
  letterSpacing: '0.04em',
} as const;

function HitsBadge({
  totalHits,
  ppdLabel,
  status,
  sport,
}: {
  totalHits: number;
  /** Credit label when the pick's game was postponed/cancelled (DKPLAT-478), else null. */
  ppdLabel: string | null;
  status: string | null;
  sport: string;
}) {
  const label = getDailyRevealConfig(sport)?.hitStatLabel ?? 'HIT';
  if (status === 'graded') {
    if (ppdLabel) {
      return (
        <span className="shrink-0 whitespace-nowrap text-[#53d337]" style={HITS_BADGE_STYLE}>
          {ppdLabel}
        </span>
      );
    }
    const won = totalHits > 0;
    return (
      <span
        className={`shrink-0 whitespace-nowrap uppercase ${won ? 'text-[#FFFFFF]' : 'text-[#EA2054]'}`}
        style={HITS_BADGE_STYLE}
      >
        {won ? `${totalHits} ${label}${totalHits !== 1 ? 'S' : ''}` : `0 ${label}S`}
      </span>
    );
  }

  if (totalHits > 0) {
    return (
      <span
        className="shrink-0 whitespace-nowrap uppercase text-[#FFFFFF]"
        style={HITS_BADGE_STYLE}
      >
        {totalHits} {label}
        {totalHits !== 1 ? 'S' : ''}
      </span>
    );
  }

  return (
    <span className="shrink-0 whitespace-nowrap uppercase text-[#FFFFFF]" style={HITS_BADGE_STYLE}>
      1+ {label}
    </span>
  );
}

function formatAbbreviatedPlayerName(
  firstName: string,
  lastName: string,
  displayName: string
): string {
  const first = (firstName || displayName.split(/\s+/)[0] || '').trim();
  const last = (lastName || displayName.split(/\s+/).slice(1).join(' ') || '').trim();
  if (first && last) return `${first.charAt(0)}. ${last}`.toUpperCase();
  return (displayName || `${first} ${last}`.trim()).toUpperCase();
}

function formatLivePeriod(fixture: DayFixture | null): string | null {
  if (!fixture || fixture.status !== 'live') return null;
  const clock = fixture.clock?.trim();
  const quarter = fixture.quarter;
  if (quarter != null && clock) return `Q${quarter} ${clock}`;
  if (clock) return clock;
  if (quarter != null) return `Q${quarter}`;
  return null;
}

function formatPickCardGameTime(
  fixture: DayFixture | null,
  propGameStartAt?: string | null
): string | null {
  if (fixture?.status === 'live') return 'LIVE';
  if (fixture?.status === 'finished') return 'FINAL';

  const fromFixture = fixture ? formatFixtureTime(fixture) : null;
  if (fromFixture) {
    return `${fromFixture.replace(/([ap]m)$/i, m => m.toUpperCase())} ET`;
  }

  if (!propGameStartAt) return null;
  try {
    return (
      new Date(propGameStartAt)
        .toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
        })
        .replace(/\s?(AM|PM)$/i, (_, m: string) => m.toUpperCase()) + ' ET'
    );
  } catch {
    return null;
  }
}

export default function UpcomingPickCard({
  pick,
  dateLabel,
  totalHits,
  ppdHits = 0,
  ppdStatus = null,
  fixture,
  entryStatus,
  isPickCutoffReached,
  onEdit,
  leagueIpEnabled = false,
  isPropBased = false,
  propResultStatus,
  propResultValue,
  propDisplayLabel,
  propThreshold,
  propOperator,
  entryId,
  dayId,
  contestSport,
  sportsbookSelectionIds = [],
  className = 'mb-3',
}: UpcomingPickCardProps) {
  const isPickLocked = isPickCutoffReached ?? false;
  const isCurrentPickFixtureLocked =
    fixture != null &&
    (fixture.status === 'live' || fixture.status === 'finished' || fixture.status === 'cancelled');
  // DKPLAT-212: the edit gate is keyed to the picked card's OWN fixture, never to the
  // day-level pickCutoffAt / day status. The day flips `open -> locked` at the *earliest*
  // fixture on the card minus a minute, which used to withdraw EDIT from every player —
  // one customer lost it 58 minutes before their own first pitch (30 Aug 2026). The
  // backend `pickCard` has no day-level guard, so this was a UI read-path defect only.
  const canEdit = entryStatus !== 'graded' && !isPickLocked && !isCurrentPickFixtureLocked;
  /** Padlock shows whenever edit is suppressed by this card's own fixture, pre-grading. */
  const showPickLock = (isPickLocked || isCurrentPickFixtureLocked) && entryStatus !== 'graded';

  const awayTeamRaw = fixture?.awayTeam ?? pick.awayTeam;
  const homeTeamRaw = fixture?.homeTeam ?? pick.homeTeam;
  const awayTeam = awayTeamRaw ? toTeamAbbreviation(awayTeamRaw) : '';
  const homeTeam = homeTeamRaw ? toTeamAbbreviation(homeTeamRaw) : '';
  const isLive = fixture?.status === 'live';

  const [nowMs, setNowMs] = useState(() => Date.now());
  const fixtureStartMs = fixture?.startTime ? new Date(fixture.startTime).getTime() : null;
  const isScheduled = fixture?.status === 'scheduled';

  useEffect(() => {
    if (!isScheduled || fixtureStartMs === null || fixtureStartMs - Date.now() <= 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (fixtureStartMs - now <= 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [isScheduled, fixtureStartMs]);

  const gameStatus = (() => {
    if (!fixture) return pick.time || '';
    if (isScheduled && fixtureStartMs !== null) {
      const remaining = fixtureStartMs - nowMs;
      if (remaining > 0) return formatCountdown(remaining);
    }
    return formatFixtureStatus(fixture);
  })();

  // `pick.position` matters for `jerseys-position` (the NFL default), whose files are keyed
  // `{TEAM}_{POS}`; without it every card would drop back to the plain team jersey.
  const displayImageUrl = leagueIpEnabled
    ? pick.playerImage
    : getJerseyAvatarUrl(pick.team, pick.sport, pick.position);

  const isGraded = entryStatus === 'graded';
  const ppdLabel = isGraded && ppdHits > 0 ? getPpdCreditLabel(ppdStatus, fixture?.status) : null;
  const threshold = resolvePropTrackerThreshold({
    propThreshold,
    propDisplayLabel: propDisplayLabel ?? pick.prop?.displayLabel,
    prop: pick.prop,
  });
  const operator = resolvePropTrackerOperator({
    propOperator,
    propDisplayLabel: propDisplayLabel ?? pick.prop?.displayLabel,
    prop: pick.prop,
  });
  const isLiveOrFinished = fixture?.status === 'live' || fixture?.status === 'finished' || isGraded;
  const trackerOutcome = resolvePropTrackerOutcome({
    propResultStatus,
    propResultValue,
    threshold,
    operator,
    totalHits,
    isGraded,
    isLiveOrFinished,
  });
  const showPropTracker =
    isPropBased &&
    threshold != null &&
    Number.isFinite(threshold) &&
    threshold > 0 &&
    (isLiveOrFinished || propResultValue != null);

  const celebration = usePropProgressCelebration({
    enabled: Boolean(showPropTracker && entryId && dayId),
    storageKey: entryId && dayId ? buildPropProgressSeenKey(entryId, dayId) : null,
    statType: inferCelebrationStatType(
      pick.prop?.statType,
      propDisplayLabel ?? pick.prop?.displayLabel
    ),
    currentValue: propResultValue,
  });
  const hasLiveHits = !isGraded && isLive && totalHits > 0;
  const borderColor = isPropBased
    ? getPropTrackerBorderClass(trackerOutcome)
    : isGraded
      ? totalHits > 0
        ? 'border-2 border-[#53D337]'
        : 'border-2 border-[#E9344A]'
      : hasLiveHits
        ? 'border-2 border-[#53D337]'
        : 'border-2 border-[#3f3f3f]';

  // One resolution for both layouts: the structured prop when the card still carries it,
  // otherwise the entry's frozen label — which is all a pick rebuilt from a stored entry
  // has to say which market it was.
  const propTrackerParts = isPropBased
    ? getPropTrackerDisplayParts({
        prop: pick.prop,
        propDisplayLabel: propDisplayLabel ?? pick.prop?.displayLabel,
        propThreshold: propThreshold ?? pick.prop?.threshold ?? null,
        propOperator,
        sport: pick.sport,
      })
    : null;
  const propSummary = propTrackerParts;
  const propGameTime = isPropBased ? formatPickCardGameTime(fixture, pick.prop?.gameStartAt) : null;
  const abbreviatedName = formatAbbreviatedPlayerName(
    pick.firstName,
    pick.lastName,
    pick.displayName
  );
  const gradedPropResult = isPropBased
    ? formatPropResultLabel(
        isGraded ? propResultStatus : 'pending',
        propResultValue,
        propDisplayLabel ?? pick.prop?.displayLabel ?? pick.hit
      )
    : null;
  const showPropHitIcon =
    showPropTracker && (trackerOutcome === 'hit' || trackerOutcome === 'miss');
  const showLiveGold = Boolean(isPropBased && isLive && showPropTracker && !isGraded);
  const livePeriod = showLiveGold ? formatLivePeriod(fixture) : null;

  // The betslip handoff is only useful while the sportsbook market is still
  // open. Once the pick locks (kickoff) or the day is graded, the outcome can no
  // longer be bet, so the CTA disappears alongside the EDIT affordance.
  const showBetslipCta =
    SPORTSBOOK_BETSLIP_ENABLED &&
    isPropBased &&
    !isGraded &&
    !isPickLocked &&
    !isCurrentPickFixtureLocked;

  return (
    <article className={className}>
      <p
        className="mb-2 pl-2 text-[#8a8a8a]"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '12px',
          fontWeight: 500,
        }}
      >
        {dateLabel}
      </p>
      <div
        className={
          showLiveGold
            ? 'rounded-[12px] p-[2px]'
            : `relative overflow-hidden rounded-[12px] bg-[#1c1c1c] ${borderColor}`
        }
        style={showLiveGold ? { backgroundImage: LIVE_GOLD_BORDER_GRADIENT } : undefined}
      >
        <div
          className={
            showLiveGold ? 'relative overflow-hidden rounded-[10px] bg-[#1c1c1c]' : 'relative'
          }
        >
          {showPropTracker ? (
            <PropProgressCelebrationOverlay
              isOpen={celebration.isPlaying}
              kind={celebration.kind}
              src={celebration.src}
              onComplete={celebration.complete}
            />
          ) : null}
          {isPropBased ? (
            showPropTracker ? (
              <div className="relative px-4 pb-2 pt-3.5">
                <div
                  className="absolute right-4 top-3.5 flex items-center gap-1.5"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '11px',
                    letterSpacing: '0.04em',
                  }}
                >
                  {showLiveGold ? (
                    <>
                      <span
                        className="inline-flex items-center gap-1 rounded-[4px] px-1.5 py-[3px]"
                        style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
                      >
                        <Radio size={11} color={LIVE_GOLD} strokeWidth={2.5} aria-hidden />
                        <span className="uppercase leading-none" style={{ color: LIVE_GOLD }}>
                          Live
                        </span>
                      </span>
                      {livePeriod ? (
                        <span className="uppercase leading-none text-white">{livePeriod}</span>
                      ) : null}
                    </>
                  ) : (
                    <span className="uppercase leading-none text-[#9a9a9a]">
                      {fixture?.status === 'finished' || isGraded ? 'Final' : (propGameTime ?? '')}
                    </span>
                  )}
                </div>

                <div
                  className={`flex items-start gap-3.5 ${showLiveGold ? 'pr-[7.5rem]' : 'pr-14'}`}
                >
                  <PlayerPickAvatar
                    displayImageUrl={displayImageUrl}
                    alt={pick.displayName || `${pick.firstName} ${pick.lastName}`}
                    initials={`${pick.firstName?.[0] ?? ''}${pick.lastName?.[0] ?? ''}`}
                    leagueIpEnabled={leagueIpEnabled}
                    teamCode={pick.team}
                    sport={pick.sport}
                    objectFit="contain"
                  />

                  <div className="min-w-0 flex-1">
                    <FitText
                      text={abbreviatedName}
                      maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                      minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                      className="uppercase leading-none text-white"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                      }}
                    />

                    <div className="mt-2 flex items-center gap-2">
                      {showPropHitIcon ? <PropOutcomeIcon outcome={trackerOutcome} /> : null}
                      {propTrackerParts ? (
                        <p
                          className="min-w-0 uppercase leading-none"
                          style={{
                            fontFamily: '"Saira Semi Condensed", sans-serif',
                            fontWeight: 700,
                            fontSize: '11px',
                            letterSpacing: '0.02em',
                          }}
                        >
                          <span
                            className="uppercase"
                            style={{ color: showLiveGold ? LIVE_GOLD : '#ffffff' }}
                          >
                            {propTrackerParts.value}
                          </span>{' '}
                          <span className="text-[#BCBCBC]">{propTrackerParts.label}</span>
                        </p>
                      ) : null}
                    </div>

                    <div className="mt-3">
                      <PropStatProgressTracker
                        currentValue={celebration.displayedValue}
                        threshold={threshold ?? 0}
                        outcome={trackerOutcome}
                        fillDurationMs={celebration.emphasizeFill ? 1000 : 500}
                        live={showLiveGold}
                      />
                    </div>

                    {(canEdit && onEdit) || showPickLock ? (
                      <div className="mt-1 flex items-center gap-2">
                        {canEdit && onEdit ? (
                          <button
                            type="button"
                            onClick={onEdit}
                            className="flex items-center gap-[5px] leading-none text-[#53d337] transition-colors hover:text-[#6be04a]"
                            style={{
                              fontFamily: '"Saira Semi Condensed", sans-serif',
                              fontWeight: 700,
                              fontSize: '13px',
                              letterSpacing: '0.06em',
                            }}
                            aria-label="Edit selection"
                          >
                            <Pencil size={13} strokeWidth={2.5} />
                            EDIT
                          </button>
                        ) : null}
                        {showPickLock && (
                          <Lock
                            size={14}
                            strokeWidth={2.5}
                            className="text-[#8a8a8a]"
                            data-test-id="pick-lock-icon"
                          />
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex min-h-[96px] items-stretch px-4 py-3.5">
                <div className="flex min-w-0 flex-[1.6] items-center gap-3.5 pr-4">
                  <PlayerPickAvatar
                    displayImageUrl={displayImageUrl}
                    alt={pick.displayName || `${pick.firstName} ${pick.lastName}`}
                    initials={`${pick.firstName?.[0] ?? ''}${pick.lastName?.[0] ?? ''}`}
                    leagueIpEnabled={leagueIpEnabled}
                    teamCode={pick.team}
                    sport={pick.sport}
                    objectFit="contain"
                  />

                  <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <FitText
                      text={abbreviatedName}
                      maxFontSize={PLAYER_NAME_MAX_FONT_SIZE}
                      minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                      className="uppercase leading-none text-white"
                      style={{
                        fontFamily: '"Saira Extra Condensed", sans-serif',
                        fontWeight: 700,
                        letterSpacing: '0.01em',
                      }}
                    />
                    <p
                      className="mt-[6px] uppercase leading-none text-[#9a9a9a]"
                      style={{
                        fontFamily: '"Saira Semi Condensed", sans-serif',
                        fontWeight: 600,
                        fontSize: '13px',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {[pick.team, pick.number, pick.position].filter(Boolean).join(' · ')}
                    </p>
                    <div className="mt-[10px] flex items-center gap-2">
                      {canEdit && onEdit ? (
                        <button
                          type="button"
                          onClick={onEdit}
                          className="flex items-center gap-[5px] leading-none text-[#53d337] transition-colors hover:text-[#6be04a]"
                          style={{
                            fontFamily: '"Saira Semi Condensed", sans-serif',
                            fontWeight: 700,
                            fontSize: '13px',
                            letterSpacing: '0.06em',
                          }}
                          aria-label="Edit selection"
                        >
                          <Pencil size={13} strokeWidth={2.5} />
                          EDIT
                        </button>
                      ) : null}
                      {showPickLock && (
                        <Lock
                          size={14}
                          strokeWidth={2.5}
                          className="text-[#8a8a8a]"
                          data-test-id="pick-lock-icon"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="my-1 w-px shrink-0 bg-[#4a4a4a]" aria-hidden />

                {/*
                  Sized to its own content between a floor and the old fixed
                  124px rather than always taking 124px. The prop line is the
                  short half of this card ("1+ ATD"), and every pixel it
                  was holding spare is a pixel the player's name did not have.
                  Keeping the cap means a wider fallback font still has room to
                  grow into instead of spilling over the divider.
                */}
                <div className="flex min-w-[92px] max-w-[124px] shrink-0 flex-col items-end py-0.5 pl-4">
                  <p
                    className={`shrink-0 leading-none ${isLive ? 'text-[#53d337]' : 'text-[#9a9a9a]'}`}
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      fontSize: '11px',
                    }}
                  >
                    {isLive ? (
                      <>
                        <span className="mr-1 inline-block h-[6px] w-[6px] rounded-full bg-[#53d337]" />
                        LIVE
                      </>
                    ) : (
                      (propGameTime ?? '\u00A0')
                    )}
                  </p>

                  <div className="flex min-h-0 flex-1 flex-col items-end justify-center">
                    {isGraded && gradedPropResult ? (
                      <p
                        className={`uppercase leading-none ${
                          gradedPropResult.won
                            ? 'text-[#53d337]'
                            : propResultStatus === 'void'
                              ? 'text-[#8a8a8a]'
                              : 'text-[#EA2054]'
                        }`}
                        style={{
                          fontFamily: '"Saira Extra Condensed", sans-serif',
                          fontWeight: 700,
                          fontSize: '24px',
                        }}
                      >
                        {gradedPropResult.label}
                      </p>
                    ) : propSummary ? (
                      <p
                        className="leading-none text-white"
                        style={{
                          fontFamily: '"Saira Extra Condensed", sans-serif',
                          fontWeight: 700,
                          fontSize: '32px',
                          letterSpacing: '0.01em',
                        }}
                      >
                        {propSummary.value}
                      </p>
                    ) : null}
                  </div>

                  {!(isGraded && gradedPropResult) && propSummary ? (
                    <p
                      className="shrink-0 uppercase leading-none text-[#9a9a9a]"
                      style={{
                        fontFamily: '"Saira Semi Condensed", sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {propSummary.label}
                    </p>
                  ) : (
                    <span className="shrink-0 leading-none opacity-0" aria-hidden>
                      .
                    </span>
                  )}
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center px-4 py-3">
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <PlayerPickAvatar
                  displayImageUrl={displayImageUrl}
                  alt={pick.displayName || `${pick.firstName} ${pick.lastName}`}
                  initials={`${pick.firstName?.[0] ?? ''}${pick.lastName?.[0] ?? ''}`}
                  leagueIpEnabled={leagueIpEnabled}
                  teamCode={pick.team}
                  sport={pick.sport}
                />

                <div className="min-w-0 flex-1">
                  <FitText
                    text={`${pick.firstName} ${pick.lastName}`.trim()}
                    maxFontSize={18.75}
                    minFontSize={PLAYER_NAME_MIN_FONT_SIZE}
                    className="uppercase leading-tight text-[#ffffff]"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 700,
                    }}
                  />
                  <p
                    className="mt-0.5 text-[#8a8a8a]"
                    style={{
                      fontFamily: '"Saira Extra Condensed", sans-serif',
                      fontWeight: 700,
                      fontSize: '16px',
                    }}
                  >
                    {pick.team}
                    {pick.number ? ` • ${pick.number}` : ''}
                    {pick.position ? ` • ${pick.position}` : ''}
                  </p>
                  <div className="mt-2 flex flex-nowrap items-center gap-2">
                    <HitsBadge
                      totalHits={totalHits}
                      ppdLabel={ppdLabel}
                      status={entryStatus}
                      sport={pick.sport}
                    />
                    {canEdit && onEdit && (
                      <button
                        type="button"
                        onClick={onEdit}
                        className="flex items-center gap-1 rounded px-2 py-1 text-[12px] font-semibold uppercase text-[#53d337] transition-colors hover:bg-[#53d337]/10"
                        aria-label="Edit selection"
                      >
                        <Pencil size={12} strokeWidth={2.5} />
                        Edit
                      </button>
                    )}
                    {showPickLock && (
                      <Lock
                        size={14}
                        strokeWidth={2.5}
                        className="ml-1 text-[#8a8a8a]"
                        data-test-id="pick-lock-icon"
                      />
                    )}
                  </div>
                </div>
              </div>

              <div
                className="mx-4 w-px shrink-0 self-stretch"
                style={{ backgroundColor: '#868686' }}
                aria-hidden
              />

              <div className="flex w-[100px] shrink-0 flex-col items-end">
                <p
                  className={`mb-2 ${isLive ? 'text-[#53d337]' : 'text-[#8a8a8a]'}`}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '9px',
                  }}
                >
                  {isLive && (
                    <span className="mr-1 inline-block h-[6px] w-[6px] rounded-full bg-[#53d337]" />
                  )}
                  {gameStatus}
                </p>
                <div
                  className="grid grid-cols-[18px_1fr_auto] gap-x-2 gap-y-1"
                  style={{
                    fontFamily: '"Saira Extra Condensed", sans-serif',
                    fontWeight: 700,
                    fontSize: '18px',
                  }}
                >
                  {awayTeam && (
                    <>
                      <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
                      <span className="min-w-0 truncate text-right uppercase text-[#ffffff]">
                        {awayTeam}
                      </span>
                      <span className="w-6 text-right tabular-nums text-[#ffffff]">
                        {fixture?.awayScore != null ? fixture.awayScore : '-'}
                      </span>
                    </>
                  )}
                  {homeTeam && (
                    <>
                      <div className="flex h-[18px] w-[18px] shrink-0 items-center justify-center" />
                      <span className="min-w-0 truncate text-right uppercase text-[#ffffff]">
                        {homeTeam}
                      </span>
                      <span className="w-6 text-right tabular-nums text-[#ffffff]">
                        {fixture?.homeScore != null ? fixture.homeScore : '-'}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showBetslipCta ? (
        <SportsbookBetslipCta sport={contestSport} selectionIds={sportsbookSelectionIds} />
      ) : null}
    </article>
  );
}
